import { useState, useMemo, useEffect, useRef, memo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDataGet, fetchDataPost } from "../../controler/comunicacao";
import LoadingModal from "../../components/LoadingModal/LoadingModal.jsx";
import CardChart from "../../components/Card/Card.jsx";
import ChartSeriesTemporal from "../../components/Charts/ChartSeriesTemporal.jsx";
import localidades from "../../components/Filter/cidades";
import municipioCodigos from "../../components/Filter/municipioCodigos";
import "../Dashboard.css";

const STALE_EVAL = 1000 * 60 * 60 * 24 * 15;

// Mapeamentos estáticos (não mudam)
const REGIAO_CODIGO = { "Norte": "1", "Nordeste": "2", "Sudeste": "3", "Sul": "4", "Centro-Oeste": "5" };
const UF_IBGE = {
  AC:"12",AL:"27",AM:"13",AP:"16",BA:"29",CE:"23",DF:"53",ES:"32",GO:"52",MA:"21",
  MG:"31",MS:"50",MT:"51",PA:"15",PB:"25",PE:"26",PI:"22",PR:"41",RJ:"33",RN:"24",
  RO:"11",RR:"14",RS:"43",SC:"42",SE:"28",SP:"35",TO:"17",
};
const REGIOES = Object.keys(localidades).sort();

// ── Utilitários ────────────────────────────────────────────────────────────────
function fN(v, d = 3) {
  if (v == null || isNaN(v)) return "—";
  return Number(v).toLocaleString("pt-BR", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
}
function fP(v) {
  if (v == null) return "—";
  return v < 0.001 ? Number(v).toExponential(2) : fN(v, 4);
}
function resultadoBadgeStyle(resultado) {
  if (!resultado) return { background: "rgba(139,92,246,0.15)", color: "#a78bfa", border: "1px solid rgba(139,92,246,0.4)" };
  if (resultado.includes("Crescente"))   return { background: "rgba(239,68,68,0.12)",  color: "#f87171", border: "1px solid rgba(239,68,68,0.35)" };
  if (resultado.includes("Decrescente")) return { background: "rgba(52,211,153,0.12)", color: "#34d399", border: "1px solid rgba(52,211,153,0.35)" };
  if (resultado === "Estacionária")      return { background: "rgba(96,165,250,0.12)", color: "#60a5fa", border: "1px solid rgba(96,165,250,0.35)" };
  if (resultado === "Não estacionária")  return { background: "rgba(251,191,36,0.12)", color: "#fbbf24", border: "1px solid rgba(251,191,36,0.35)" };
  return { background: "rgba(139,92,246,0.15)", color: "#a78bfa", border: "1px solid rgba(139,92,246,0.4)" };
}
function Badge({ text }) {
  const s = resultadoBadgeStyle(text);
  return (
    <span style={{ ...s, fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 12 }}>
      {text}
    </span>
  );
}

// ── Modal de confirmação de processamento ──────────────────────────────────────
function ModalProcessar({ info, onConfirm, onCancel }) {
  if (!info) return null;
  const tempoEst = info.has_model ? "2 a 5 minutos" : "30 a 60 minutos";
  const descricao = info.has_model
    ? "Modelos já treinados encontrados. Será executada apenas a inferência."
    : "Nenhum modelo treinado encontrado para este recorte. O pipeline completo (pré-processamento + treinamento + avaliação) será executado.";

  return (
    <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.6)" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Processamento Necessário</h5>
          </div>
          <div className="modal-body">
            <p><strong>{info.label}</strong> ainda não possui dados pré-computados.</p>
            <p style={{ marginTop: 8 }}>{descricao}</p>
            <div style={{
              marginTop: 14, padding: "10px 14px",
              background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)",
              borderRadius: 8, fontSize: 13,
            }}>
              ⏱ Tempo estimado: <strong>{tempoEst}</strong>
            </div>
            <p style={{ marginTop: 12, fontSize: 12, color: "var(--text-50)" }}>
              O processamento ocorre em segundo plano. Não feche a página.
            </p>
          </div>
          <div className="modal-footer">
            <button className="btn btn-sm btn-outline-secondary" onClick={onCancel}>Cancelar</button>
            <button className="btn btn-sm btn-primary" onClick={onConfirm}>Iniciar Processamento</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Modal de progresso ─────────────────────────────────────────────────────────
function ModalProgresso({ label, has_model }) {
  const msg = has_model ? "Executando inferência com modelos salvos…" : "Treinando modelos e computando previsões…";
  return (
    <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.6)" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content text-center" style={{ padding: "32px 24px" }}>
          <div className="spinner-border text-primary mb-3" style={{ width: 48, height: 48 }} />
          <h5>{label}</h5>
          <p style={{ color: "var(--text-50)", fontSize: 13, marginTop: 8 }}>{msg}</p>
          <p style={{ color: "var(--text-50)", fontSize: 12 }}>Aguarde — este processo pode demorar alguns minutos.</p>
        </div>
      </div>
    </div>
  );
}

// ── Disponibilidade pré-computada (carregada uma vez) ─────────────────────────
function useDisponivel() {
  return useQuery({
    queryKey: ["st-disponivel"],
    queryFn:  () => fetchDataGet("/api/series-temporais/disponivel"),
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });
}

// ── Filtros (mesmo padrão do TB-notificacao — tudo estático/instantâneo) ───────
const NIVEIS = [
  { id: 1, label: "Nacional"    },
  { id: 2, label: "Região"      },
  { id: 3, label: "Estado (UF)" },
  { id: 4, label: "Município"   },
];

function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: "7px 18px", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: "pointer",
      border: "1px solid",
      borderColor: active ? "var(--secondary-500,#3b82f6)" : "var(--background-200,#2e3350)",
      background:  active ? "var(--secondary-500,#3b82f6)" : "transparent",
      color:       active ? "#fff" : "var(--text-50)",
      transition: "all .15s",
    }}>{children}</button>
  );
}

function RadioList({ items, value, onChange, badge }) {
  return (
    <div style={{ maxHeight: 240, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
      {items.map((item) => (
        <label key={item.key} style={{
          display: "flex", alignItems: "center", gap: 8, padding: "5px 8px",
          borderRadius: 5, cursor: "pointer", fontSize: 13,
          background: value === item.key ? "rgba(59,130,246,0.12)" : "transparent",
        }}>
          <input type="radio" name="st-opcao" checked={value === item.key}
            onChange={() => onChange(item)} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1 }}>{item.label}</span>
          {badge && badge(item)}
        </label>
      ))}
    </div>
  );
}

function SeriesTemporaisFilters({ onGerar, disponivelData = [] }) {
  const [nivelId,    setNivelId]    = useState(1);
  const [regiaoSel,  setRegiaoSel]  = useState(null);
  const [ufSel,      setUfSel]      = useState(null);
  const [municSel,   setMunicSel]   = useState(null);
  const [busca,      setBusca]      = useState("");

  // Conjuntos de slugs pré-computados para badges
  const precomputados = useMemo(
    () => new Set((disponivelData ?? []).map((d) => d.slug).filter(Boolean)),
    [disponivelData]
  );

  // Lista de UFs da região selecionada
  const ufsDisponiveis = useMemo(() => {
    if (!regiaoSel) return [];
    return Object.keys(localidades[regiaoSel] ?? {}).sort();
  }, [regiaoSel]);

  // Lista de municípios da UF selecionada
  const municipiosBrutos = useMemo(() => {
    if (!regiaoSel || !ufSel) return [];
    return (localidades[regiaoSel]?.[ufSel] ?? []).sort();
  }, [regiaoSel, ufSel]);

  const municipiosFiltrados = useMemo(() => {
    if (!busca.trim()) return municipiosBrutos;
    const q = busca.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    return municipiosBrutos.filter((m) =>
      m.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(q)
    );
  }, [municipiosBrutos, busca]);

  function handleNivel(id) {
    setNivelId(id);
    setRegiaoSel(null); setUfSel(null); setMunicSel(null); setBusca("");
  }

  function handleRegiao(item) {
    setRegiaoSel(item.key); setUfSel(null); setMunicSel(null); setBusca("");
  }
  function handleUf(item) {
    setUfSel(item.key); setMunicSel(null); setBusca("");
  }

  // Constrói payload para enviar ao parent
  const payload = useMemo(() => {
    if (nivelId === 1) return { nivel: "brasil", codigo: null, label: "Brasil" };
    if (nivelId === 2 && regiaoSel)
      return { nivel: "regiao", codigo: REGIAO_CODIGO[regiaoSel], label: regiaoSel };
    if (nivelId === 3 && ufSel)
      return { nivel: "uf", codigo: UF_IBGE[ufSel], label: ufSel };
    if (nivelId === 4 && municSel) {
      const codigo = municipioCodigos[`${municSel}|${ufSel}`];
      return { nivel: "municipio", codigo: codigo ?? null, label: `${municSel} - ${ufSel}` };
    }
    return null;
  }, [nivelId, regiaoSel, ufSel, municSel]);

  // Badge "pronto" se já existe JSON pré-computado
  function badgePrecomp(slug) {
    if (!precomputados.has(slug)) return null;
    return <span style={{ fontSize: 10, background: "rgba(16,185,129,0.15)", color: "#10b981", padding: "1px 6px", borderRadius: 8 }}>pronto</span>;
  }

  return (
    <div className="container-fluid px-0 pb-3">
      {/* Tabs de nível */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        {NIVEIS.map((n) => (
          <TabBtn key={n.id} active={nivelId === n.id} onClick={() => handleNivel(n.id)}>
            {n.label}
          </TabBtn>
        ))}
      </div>

      {/* Cards de seleção — mesma lógica cascata do Filter.jsx */}
      <div className="d-flex overflow-auto gap-3 pb-2 align-items-start">

        {/* Região — visível para Região/UF/Município */}
        {nivelId >= 2 && (
          <div className="card flex-shrink-0" style={{ minWidth: 200 }}>
            <div className="card-body" style={{ padding: "12px 14px", boxShadow: "none" }}>
              <h6 className="card-title mb-2">Região</h6>
              <RadioList
                items={REGIOES.map((r) => ({ key: r, label: r }))}
                value={regiaoSel}
                onChange={handleRegiao}
                badge={(item) => badgePrecomp(`Regiao_${REGIAO_CODIGO[item.key]}`)}
              />
            </div>
          </div>
        )}

        {/* Estado — visível para UF/Município, após selecionar região */}
        {nivelId >= 3 && regiaoSel && (
          <div className="card flex-shrink-0" style={{ minWidth: 160 }}>
            <div className="card-body" style={{ padding: "12px 14px", boxShadow: "none" }}>
              <h6 className="card-title mb-2">Estado (UF)</h6>
              <RadioList
                items={ufsDisponiveis.map((uf) => ({ key: uf, label: uf }))}
                value={ufSel}
                onChange={handleUf}
                badge={(item) => badgePrecomp(`UF_${UF_IBGE[item.key]}`)}
              />
            </div>
          </div>
        )}

        {/* Município — visível após selecionar UF */}
        {nivelId === 4 && ufSel && (
          <div className="card flex-shrink-0" style={{ minWidth: 260 }}>
            <div className="card-body" style={{ padding: "12px 14px", boxShadow: "none" }}>
              <h6 className="card-title mb-2">Município</h6>
              <div style={{ position: "relative", marginBottom: 8 }}>
                <input
                  className="form-control form-control-sm"
                  placeholder="Pesquisar município..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  autoFocus
                />
                {busca && (
                  <button onClick={() => setBusca("")} style={{
                    position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", color: "var(--text-50)", fontSize: 14,
                  }}>✕</button>
                )}
              </div>
              <RadioList
                items={municipiosFiltrados.map((m) => ({ key: m, label: m }))}
                value={municSel}
                onChange={(item) => setMunicSel(item.key)}
                badge={(item) => {
                  const cod = municipioCodigos[`${item.key}|${ufSel}`];
                  return badgePrecomp(`Municipio_${cod}`);
                }}
              />
              {municipiosFiltrados.length === 0 && (
                <div style={{ fontSize: 12, color: "var(--text-50)", padding: "4px 0" }}>
                  Nenhum município encontrado.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="d-flex gap-2 mt-2">
        <button className="btn btn-sm btn-success" onClick={() => payload && onGerar(payload)} disabled={!payload}>
          Gerar Análise
        </button>
        <button className="btn btn-sm btn-outline-secondary"
          onClick={() => { handleNivel(1); }}>
          Limpar
        </button>
      </div>
    </div>
  );
}

// ── KPI Grid ───────────────────────────────────────────────────────────────────
const KPIGrid = memo(function KPIGrid({ data }) {
  const ds    = data.dataset;
  const melhor = data.melhor_modelo;
  const tend   = data.analise_tendencia?.historica?.resumo?.resultado_final ?? "—";
  const tendComp = data.analise_tendencia?.completa?.resumo?.resultado_final ?? "—";
  const tendColor =
    tend === "Crescente"  ? "#f87171"
    : tend === "Decrescente" ? "#34d399"
    : "var(--secondary-500, #3b82f6)";

  return (
    <div className="row g-3 mb-3">
      {[
        {
          label: "Ocorrências na Base",
          value: ds.numero_ocorrencias_linhas?.toLocaleString("pt-BR") ?? "—",
          sub: `${ds.intervalo_dados_reais?.inicio?.slice(0,7) ?? ""} — ${ds.intervalo_dados_reais?.fim?.slice(0,7) ?? ""}`,
          color: "var(--secondary-500, #3b82f6)",
        },
        { label: "Melhor Modelo", value: melhor.nome, sub: "Menor SMAPE na validação (24 meses)", color: "var(--primary-500, #10b981)", small: true },
        {
          label: "SMAPE — Validação",
          value: melhor.metrics?.SMAPE != null ? fN(melhor.metrics.SMAPE, 2) + "%" : "—",
          sub: `MAPE: ${melhor.metrics?.MAPE != null ? fN(melhor.metrics.MAPE, 2) + "%" : "—"}`,
          color: "var(--accent-500, #f59e0b)",
        },
        {
          label: "Pontos Previstos",
          value: ds.numero_pontos_previstos ?? "—",
          sub: `${ds.intervalo_dados_previstos?.inicio?.slice(0,7) ?? ""} — ${ds.intervalo_dados_previstos?.fim?.slice(0,7) ?? ""}`,
          color: "#8b5cf6",
        },
        {
          label: "Tendência Histórica",
          value: tend + (tend === "Crescente" ? " ↑" : tend === "Decrescente" ? " ↓" : ""),
          sub: `Série completa: ${tendComp}`,
          color: tendColor, small: true,
        },
      ].map(({ label, value, sub, color, small }) => (
        <div className="col-6 col-md-4 col-xl" key={label}>
          <div className="card h-100">
            <div className="card-body" style={{ padding: "14px 16px" }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".07em", color: "var(--text-50)", marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: small ? 17 : 24, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
              <div style={{ fontSize: 11, color: "var(--text-50)", marginTop: 4 }}>{sub}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
});

// ── Painel Análise de Tendência ────────────────────────────────────────────────
const TendenciaPanel = memo(function TendenciaPanel({ analise }) {
  const SECOES = [
    { key: "historica", label: "Série Histórica" },
    { key: "prevista",  label: "Série Prevista" },
    { key: "completa",  label: "Série Completa" },
  ];
  return (
    <div className="card h-100">
      <div className="card-header"><h5 className="m-0">Análise de Tendência</h5></div>
      <div className="card-body" style={{ padding: "12px 16px", overflowY: "auto", maxHeight: 480 }}>
        {SECOES.map(({ key, label }) => {
          const sec = analise?.[key];
          if (!sec) return null;
          const res = sec.resumo?.resultado_final ?? "—";
          const est = sec.resumo?.resultado_estacionariedade ?? "—";
          return (
            <div key={key} style={{ marginBottom: 16, padding: "12px 14px", background: "linear-gradient(135deg,rgba(59,130,246,0.07),rgba(139,92,246,0.04))", borderRadius: 8, border: "1px solid rgba(59,130,246,0.15)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-50)", textTransform: "uppercase" }}>{label}</span>
                <Badge text={res} />
                <Badge text={est} />
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, color: "var(--text-50)" }}>
                  <thead>
                    <tr>
                      {["Teste","Estatística","p-valor","Resultado"].map((h) => (
                        <th key={h} style={{ color: "var(--text-50)", fontWeight: 500, padding: "4px 8px", borderBottom: "1px solid rgba(128,128,128,0.2)", textAlign: "left", fontSize: 11, textTransform: "uppercase" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(sec.testes ?? []).map((t, i) => (
                      <tr key={i}>
                        <td style={{ padding: "5px 8px", color: "var(--text-50)" }}>{t.teste}</td>
                        <td style={{ padding: "5px 8px", fontFamily: "monospace", fontSize: 11, color: "var(--text-50)" }}>{fN(t.estatistica, 4)}</td>
                        <td style={{ padding: "5px 8px", fontFamily: "monospace", fontSize: 11, color: "var(--text-50)" }}>{fP(t.p_valor)}</td>
                        <td style={{ padding: "5px 8px" }}><Badge text={t.resultado} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

// ── Painel Melhor Modelo ───────────────────────────────────────────────────────
const MelhorModeloPanel = memo(function MelhorModeloPanel({ melhor, modelos }) {
  const nome   = melhor?.nome;
  const params = nome && modelos?.[nome]?.best_params;
  const m      = melhor?.metrics ?? {};
  return (
    <div className="card h-100">
      <div className="card-header"><h5 className="m-0">Melhor Modelo Selecionado</h5></div>
      <div className="card-body" style={{ padding: "16px 20px" }}>
        <div style={{ background: "linear-gradient(135deg,rgba(59,130,246,.12),rgba(139,92,246,.08))", border: "1px solid rgba(59,130,246,.3)", borderRadius: 10, padding: "20px 24px", marginBottom: 20 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#60a5fa", marginBottom: 4 }}>{nome ?? "—"}</div>
          <div style={{ fontSize: 12, color: "var(--text-50)", marginBottom: 16 }}>
            AIC: <strong>{melhor?.AIC != null ? fN(melhor.AIC, 2) : "—"}</strong>
            &nbsp;&nbsp;BIC: <strong>{melhor?.BIC != null ? fN(melhor.BIC, 2) : "—"}</strong>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
            {[["SMAPE",m.SMAPE,"%"],["MAPE",m.MAPE,"%"],["RMSE",m.RMSE,""],["MAE",m.MAE,""]].map(([n,v,u]) => (
              <div key={n} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{v != null ? fN(v, 3)+u : "—"}</div>
                <div style={{ fontSize: 10, color: "var(--text-50)", textTransform: "uppercase", marginTop: 2 }}>{n}</div>
              </div>
            ))}
          </div>
        </div>
        {params && (
          <>
            <div style={{ fontSize: 12, color: "var(--text-50)", marginBottom: 6 }}>Hiperparâmetros</div>
            <div style={{ fontFamily: "monospace", fontSize: 12, background: "rgba(59,130,246,0.06)", borderRadius: 6, padding: "10px 12px", lineHeight: 1.9 }}>
              {Object.entries(params).map(([k,v]) => (
                <div key={k}>
                  <span style={{ color: "var(--secondary-500, #3b82f6)" }}>{k}</span>
                  {": "}
                  <span style={{ color: "var(--accent-500, #f59e0b)" }}>{JSON.stringify(v)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

// ── Alerta de alta incerteza (SMAPE > 35%) ────────────────────────────────────
function AlertaIncerteza({ smape }) {
  if (!smape || smape <= 35) return null;
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 14,
      background: "linear-gradient(135deg, rgba(245,158,11,0.13), rgba(239,68,68,0.08))",
      border: "1px solid rgba(245,158,11,0.45)",
      borderLeft: "4px solid #f59e0b",
      borderRadius: 10, padding: "14px 20px", marginBottom: 20,
    }}>
      <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: 22, color: "#f59e0b", flexShrink: 0, marginTop: 1 }} />
      <div>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#f59e0b", marginBottom: 4 }}>
          Previsão com Alta Incerteza
        </div>
        <div style={{ fontSize: 13, color: "var(--text-50)", lineHeight: 1.5 }}>
          O número de observações por ocorrência é insuficiente para um modelo de alta confiança.
          O melhor modelo obteve <strong style={{ color: "#f87171" }}>SMAPE de {fN(smape, 1)}%</strong> — valores acima de 35% indicam baixa acurácia preditiva.
          Interprete as previsões com cautela.
        </div>
      </div>
    </div>
  );
}

// ── Tabela Ranking ─────────────────────────────────────────────────────────────
const RankingTable = memo(function RankingTable({ ranking = [], melhorNome }) {
  const medals = ["🥇","🥈","🥉"];
  return (
    <div className="card">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h5 className="m-0">Ranking de Modelos Avaliados</h5>
        <span style={{ fontSize: 12, color: "var(--text-50)" }}>{ranking.length} modelos · ordenado por SMAPE</span>
      </div>
      <div className="card-body" style={{ padding: 0, overflowX: "auto" }}>
        <table className="table table-sm mb-0" style={{ fontSize: "0.82rem" }}>
          <thead>
            <tr>
              {["#","Modelo","SMAPE","MAPE","RMSE","MAE","MSE","AIC"].map((h) => (
                <th key={h} style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em", padding: "8px 10px" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ranking.map((m, i) => {
              const isBest = m.model_name === melhorNome;
              return (
                <tr key={m.model_name} style={{ background: isBest ? "rgba(59,130,246,.07)" : undefined }}>
                  <td style={{ padding: "7px 10px", color: "var(--text-50)", fontWeight: 600 }}>{i+1}</td>
                  <td style={{ padding: "7px 10px", fontWeight: isBest ? 700 : 400, color: isBest ? "var(--secondary-500, #3b82f6)" : undefined }}>
                    {medals[i] ?? ""} {m.model_name}
                    {isBest && <span style={{ background: "var(--secondary-500,#3b82f6)", color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 8, marginLeft: 6 }}>MELHOR</span>}
                  </td>
                  <td style={{ padding: "7px 10px" }}>
                    <span style={{ background: "rgba(59,130,246,0.08)", padding: "2px 8px", borderRadius: 4, fontFamily: "monospace", fontSize: 11, color: i < 3 ? "var(--primary-500,#10b981)" : undefined, fontWeight: i < 3 ? 700 : 400 }}>
                      {m.SMAPE != null ? fN(m.SMAPE, 3)+"%" : "—"}
                    </span>
                  </td>
                  <td style={{ padding: "7px 10px" }}>{m.MAPE  != null ? fN(m.MAPE,  3)+"%" : "—"}</td>
                  <td style={{ padding: "7px 10px" }}>{m.RMSE  != null ? fN(m.RMSE,  4)    : "—"}</td>
                  <td style={{ padding: "7px 10px" }}>{m.MAE   != null ? fN(m.MAE,   4)    : "—"}</td>
                  <td style={{ padding: "7px 10px" }}>{m.MSE   != null ? fN(m.MSE,   4)    : "—"}</td>
                  <td style={{ padding: "7px 10px", fontFamily: "monospace", fontSize: 11 }}>{m.AIC != null ? fN(m.AIC, 2) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
});

// ── Página Principal ───────────────────────────────────────────────────────────
export default function SeriesTemporais() {
  const [filtersOpen,    setFiltersOpen]    = useState(true);
  const [params,         setParams]         = useState({ nivel: "brasil", codigo: null });
  const [selectedLabel,  setSelectedLabel]  = useState("Brasil");
  const [pendingPayload, setPendingPayload] = useState(null);
  const [jobId,          setJobId]          = useState(null);
  const [jobHasModel,    setJobHasModel]    = useState(false);
  const pollRef = useRef(null);

  // Lista de slugs pré-computados
  const disponivelQ = useDisponivel();
  const disponivelData = disponivelQ.data ?? [];
  const precomputadoSlugs = useMemo(
    () => new Set(disponivelData.map((d) => d.slug).filter(Boolean)),
    [disponivelData]
  );

  // Evaluation
  const evalQ = useQuery({
    queryKey:  ["st-eval", params?.nivel ?? "", params?.codigo ?? ""],
    queryFn:   () => fetchDataPost("/api/series-temporais/evaluation", params),
    enabled:   !!params,
    staleTime: STALE_EVAL,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const data        = evalQ.isSuccess ? evalQ.data : null;
  const anyLoading  = evalQ.isLoading;
  const evalNotFound = evalQ.isError;

  const dadosObservados = useMemo(() => data?.dados_reais_observados          ?? [], [data]);
  const dadosPrevistos  = useMemo(() => data?.inferencia_proximos_24_meses    ?? [], [data]);
  const chartData = useMemo(() => !data ? null : ({
    label:        selectedLabel,
    melhor_modelo: data.melhor_modelo?.nome,
    smape:         data.melhor_modelo?.metrics?.SMAPE,
    tendencia:     data.analise_tendencia?.historica?.resumo?.resultado_final,
  }), [data, selectedLabel]);

  // Polling de job
  function stopPoll() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }
  useEffect(() => () => stopPoll(), []);

  async function startPoll(jid) {
    pollRef.current = setInterval(async () => {
      try {
        const status = await fetchDataGet(`/api/series-temporais/job/${jid}`);
        if (status.status === "done") {
          stopPoll();
          setJobId(null);
          // Forçar re-fetch
          const payload = pendingPayload;
          setPendingPayload(null);
          setParams({ ...payload });
          setSelectedLabel(payload.label);
          setFiltersOpen(false);
        } else if (status.status === "error") {
          stopPoll();
          setJobId(null);
          setPendingPayload(null);
          alert("Erro no processamento: " + (status.message ?? "desconhecido"));
        }
      } catch (_) {}
    }, 8000);
  }

  // Quando usuário clica "Gerar Análise"
  function handleGerar(payload) {
    // Deriva slug para checar se já existe JSON pré-computado
    const slugMap = { brasil: "Brasil", regiao: `Regiao_${payload.codigo}`, uf: `UF_${payload.codigo}`, municipio: `Municipio_${payload.codigo}` };
    const slug = slugMap[payload.nivel] ?? null;
    const jaCom = payload.nivel === "brasil" || (slug && precomputadoSlugs.has(slug));

    if (jaCom) {
      setParams({ nivel: payload.nivel, codigo: payload.codigo });
      setSelectedLabel(payload.label);
      setFiltersOpen(false);
    } else {
      // Verifica se tem modelo treinado (info vem do 404 da evaluation, mas podemos verificar via has_model do evalQ quando ocorrer)
      setPendingPayload({ ...payload, has_model: false });
    }
  }

  // Quando evaluation retorna 404 após setar params, captura o has_model
  useEffect(() => {
    if (evalQ.isError && evalQ.error?.response?.data?.has_model != null) {
      // Atualiza pendingPayload com has_model real do servidor
    }
  }, [evalQ.isError]);

  // Confirmar processamento
  async function handleConfirmarProcessar() {
    if (!pendingPayload) return;
    // Consulta status real antes de iniciar
    try {
      const resp404 = await fetchDataPost("/api/series-temporais/evaluation", {
        nivel: pendingPayload.nivel, codigo: pendingPayload.codigo
      }).catch((e) => e?.response?.data ?? {});
      const hasModel = resp404?.has_model ?? false;
      setJobHasModel(hasModel);
      setPendingPayload((p) => ({ ...p, has_model: hasModel }));
    } catch (_) {}
    setJobHasModel(pendingPayload.has_model ?? false);
    try {
      const resp = await fetchDataPost("/api/series-temporais/processar", {
        nivel: pendingPayload.nivel,
        codigo: pendingPayload.codigo,
      });
      const jid = resp.job_id;
      setJobId(jid);
      startPoll(jid);
    } catch (e) {
      setPendingPayload(null);
      alert("Erro ao iniciar processamento.");
    }
  }

  const showConfirmModal   = !!pendingPayload && !jobId;
  const showProgressModal  = !!jobId;

  return (
    <section className="container-fluid dashboard px-0">

      {/* Modais */}
      {showConfirmModal && (
        <ModalProcessar
          info={pendingPayload}
          onConfirm={handleConfirmarProcessar}
          onCancel={() => setPendingPayload(null)}
        />
      )}
      {showProgressModal && (
        <ModalProgresso label={pendingPayload?.label ?? selectedLabel} has_model={jobHasModel} />
      )}

      {/* Cabeçalho */}
      <div className="titulo p-3 d-flex justify-content-between align-items-center">
        <div className="titulo-nome">
          Análise Preditiva — Séries Temporais
          {selectedLabel && (
            <span style={{ fontSize: 14, fontWeight: 400, marginLeft: 10, color: "var(--text-50)" }}>
              — {selectedLabel}
            </span>
          )}
        </div>
        <button className="filter-button" onClick={() => setFiltersOpen((o) => !o)}>
          {filtersOpen ? "Ocultar Filtros" : "Filtros"}
        </button>
      </div>

      {/* Filtros */}
      {filtersOpen && (
        <div className="px-3 pt-3">
          <SeriesTemporaisFilters onGerar={handleGerar} disponivelData={disponivelData} />
        </div>
      )}

      {/* Loading query */}
      <LoadingModal
        show={anyLoading && !showProgressModal}
        onHide={() => {}}
      />

      {/* Conteúdo */}
      {data && !anyLoading && (
        <div className="px-3 pb-4 mt-3">
          <KPIGrid data={data} />

          <AlertaIncerteza smape={data.melhor_modelo?.metrics?.SMAPE} />

          <CardChart
            title={`Série Temporal de Incidência — ${selectedLabel}`}
            tooltipText="Dados observados mensais e previsão do melhor modelo (24 meses). Incidência / 100k hab."
            modalId="st-chart-main"
            loading={false}
            cols="col-12 mb-3"
            chartData={chartData}
            bodyStyle={{ height: 520 }}
          >
            <ChartSeriesTemporal
              dadosObservados={dadosObservados}
              dadosPrevistos={dadosPrevistos}
            />
          </CardChart>

          <div className="row g-3 mb-3">
            <div className="col-12 col-xl-7">
              <TendenciaPanel analise={data.analise_tendencia} />
            </div>
            <div className="col-12 col-xl-5">
              <MelhorModeloPanel melhor={data.melhor_modelo} modelos={data.modelos_avaliados} />
            </div>
          </div>

          <RankingTable ranking={data.ranking_geral ?? []} melhorNome={data.melhor_modelo?.nome} />
        </div>
      )}

      {/* Erro / não encontrado */}
      {evalNotFound && !anyLoading && (
        <div className="px-3 pt-3 text-center" style={{ color: "var(--text-50)", fontSize: 14 }}>
          Dados não pré-computados para esta seleção. Use o botão <strong>Gerar Análise</strong> para processar.
        </div>
      )}
    </section>
  );
}
